import { Navbar } from "@/components/Navbar";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Hammer, Home, Building2, MapPin, Phone, Globe, ShieldCheck, Wrench, KeyRound } from "lucide-react";

const roles = [
  { icon: Building2, title: "Acquisitions", desc: "CMT sources and evaluates each property — location, condition, occupancy and numbers — before the fund commits a dollar." },
  { icon: Wrench, title: "Rehabs", desc: "They manage the full renovation: contractors, budgets and timelines, bringing every home to market-ready condition." },
  { icon: KeyRound, title: "Rentals", desc: "For every 11th home the fund retains, CMT handles tenants, leasing and day-to-day management for steady rental income." },
];

const centralIowa = ["Des Moines metro", "Polk County", "Dallas County", "Warren County", "Story County"];
const otherMarkets = ["Johnson County", "Linn County", "Scott County", "Black Hawk County", "Council Bluffs & Omaha metro"];

export default function Management() {
  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />

      {/* Hero */}
      <section className="bg-[#0A2E26] noise-overlay">
        <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-[#C49A45] text-xs font-semibold tracking-wide uppercase">
              <ShieldCheck className="w-3.5 h-3.5" /> Our property partner
            </span>
            <h1 className="mt-6 font-display font-extrabold text-4xl sm:text-5xl text-white tracking-tight leading-tight">
              CMT Home and Property<br />Solutions <span className="text-[#C49A45]">LLC</span>
            </h1>
            <p className="mt-5 text-white/70 text-lg max-w-lg leading-relaxed">
              The Iowa-based property company that runs the fund's rehabs and rentals on the ground —
              from acquiring homes to renovating them and managing the ones we keep.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="tel:+15157782752" data-testid="cmt-call">
                <Button className="bg-[#C49A45] hover:bg-[#b0863a] text-[#0A2E26] font-semibold rounded-md h-12 px-6 gap-2">
                  <Phone className="w-4 h-4" /> (515) 778-2752
                </Button>
              </a>
              <a href="https://cmtpropertysolutionsia.com" target="_blank" rel="noopener noreferrer" data-testid="cmt-website">
                <Button variant="outline" className="border-white/25 bg-white/5 text-white hover:bg-white/10 rounded-md h-12 px-6 gap-2">
                  <Globe className="w-4 h-4" /> Visit website
                </Button>
              </a>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.15 }}
            className="rounded-2xl overflow-hidden border border-white/10">
            <img
              src="https://images.unsplash.com/photo-1721244654394-36a7bc2da288?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
              alt="Property renovation"
              className="w-full h-[360px] object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* Who's running what */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-2xl">
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0A2E26] tracking-tight">
            Who's running the rehabs & rentals
          </h2>
          <p className="mt-4 text-lg text-[#5C5C5C]">
            EquiBrick members provide the capital. CMT Home and Property Solutions LLC does the hands-on work
            of turning that capital into real, managed homes.
          </p>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-4">
          {roles.map((r, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="bg-white border border-[#E5E5E5] rounded-xl p-6 hover:-translate-y-1 hover:shadow-sm transition-transform">
              <div className="w-11 h-11 rounded-lg bg-[#0A2E26] flex items-center justify-center">
                <r.icon className="w-5 h-5 text-[#C49A45]" />
              </div>
              <h3 className="font-display font-bold text-xl text-[#0A2E26] mt-4">{r.title}</h3>
              <p className="text-sm text-[#5C5C5C] mt-2 leading-relaxed">{r.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* About + service areas */}
      <section className="max-w-7xl mx-auto px-6 pb-20 grid lg:grid-cols-2 gap-8">
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-8">
          <Home className="w-8 h-8 text-[#C49A45]" />
          <h3 className="font-display font-bold text-2xl text-[#0A2E26] mt-4">About CMT</h3>
          <p className="text-[#5C5C5C] mt-3 leading-relaxed">
            CMT Home and Property Solutions LLC is an Iowa property company that works directly with owners
            and investors across residential and commercial real estate. They review each property individually —
            location, condition, occupancy and numbers — then handle the purchase, renovation and, where the fund
            retains a home, ongoing rental management. Their approach is built on clear communication, realistic
            expectations and transactions documented in writing.
          </p>
        </div>

        <div className="bg-white border border-[#E5E5E5] rounded-xl p-8">
          <div className="flex items-center gap-2">
            <MapPin className="w-6 h-6 text-[#0A2E26]" />
            <h3 className="font-display font-bold text-2xl text-[#0A2E26]">Where they operate</h3>
          </div>
          <div className="mt-5 grid sm:grid-cols-2 gap-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-[#C49A45]">Central Iowa</p>
              <ul className="mt-2 space-y-1.5">
                {centralIowa.map((a) => (
                  <li key={a} className="text-sm text-[#1A1A1A] flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#0A2E26]" /> {a}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-[#C49A45]">Other markets</p>
              <ul className="mt-2 space-y-1.5">
                {otherMarkets.map((a) => (
                  <li key={a} className="text-sm text-[#1A1A1A] flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#0A2E26]" /> {a}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="bg-[#0A2E26] noise-overlay rounded-2xl px-8 py-14 text-center">
          <Hammer className="w-9 h-9 text-[#C49A45] mx-auto" />
          <h2 className="font-display font-extrabold text-3xl text-white mt-4">Real homes, real hands, real management.</h2>
          <p className="text-white/70 mt-3 max-w-lg mx-auto">
            With CMT running the rehabs and rentals, your $100 shares go to work on tangible property.
          </p>
          <Link to="/register" data-testid="management-register-btn">
            <Button className="mt-8 bg-[#C49A45] hover:bg-[#b0863a] text-[#0A2E26] font-semibold rounded-md h-12 px-8">
              Become a Member
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
